import { createContext, FC, PropsWithChildren, useState, useMemo, useCallback, useEffect, useRef} from 'react';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import { pick } from 'lodash';
import DraggableChatContext from '../interfaces/DraggableChatContext';
import ChatConversation from '../interfaces/ChatConversation';
import {ChanSession, ChanGroupedSessions} from '../interfaces/ChatSession';
import Message from '../interfaces/Message';
import {
    sendChatMessage,
    getChanMessageHistoryBySession,
    getChanTaskStatus,
    resumeChatMessage,
    cancelTask,
    getNewSessionName,
    ChatResponse
} from '../services';
import { isProviderKeyRequired, PROVIDER_KEY_REQUIRED_MESSAGE } from '../../GargiSettings/utils/providerKeyRequired';
import { createUserMessage, createBotMessage } from '../utils/Message';
import { DEFAULT_PLACEHOLDERS } from '../constants/suggestions';
import useUser from '../../../hooks/useUser';
import { useTranslations } from 'use-intl';

type RawApiMessage = {
  id: number;
  content: string | null;
  role: string;
  feedback: string | null;
  suggestion: string | null;
  created_at: string;
};

const DraggableChatContextValue = createContext<DraggableChatContext>({
  currentConversation: null,
  isInitializing: false,
  sessions: [],
  setSessions: () => {},
  activeSession: null,
  setActiveSession: () => {},
  isCreatingSession: false,
  createSessionError: null,
  chatTitle: 'Untitled',
  setChatTitle: () => {},
  view: 'chat',
  setView: () => {},
  mode: 'normal',
  setMode: () => {},
  inputPlaceholder: DEFAULT_PLACEHOLDERS.INITIAL,
  setInputPlaceholder: () => {},
  showFollowUp: false,
  setShowFollowUp: () => {},
  isMinimized: false,
  setIsMinimized: () => {},
  createSession: () => {},
  deleteSession: () => {},
  addUserMessage: () => {},
  resetChat: () => {},
  copyMessageToClipboard: () => { },
  currentTaskId: "",
  setCurrentTaskId: ()=> {},
  isTaskPaused:   false,
  setIsTaskPaused: ()=> {},
  suggestionMessage: '',
  setSuggestionMessage: ()=> {},
  groupedSessions: {},
  setGroupedSessions: () => {},
  regenerateChat: () => {},
  cancelTask: () => {},
  dragPosition: { 'x': 0, 'y': 0, },
  setDragPosition: () => {},
  journeyDraft: {},
  setJourneyDraft: ()=> {},
});

export const DraggableChatProvider: FC<PropsWithChildren> = ({ children }) => {
  const t = useTranslations('chan');
  const [conversations, setConversations] = useState<Record<string, ChatConversation>>({});
  const [sessions, setSessions] = useState<ChanSession[]>([]);
  const [activeSession, setActiveSession] = useState({id: "temp-session", name: "Untitled",  createdAt: "", updatedAt: ""});
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [createSessionError,] = useState<string | null>(null);
  const [chatTitle, setChatTitle] = useState('Untitled');
  const [view, setView] = useState<'chat' | 'pastChats'>('chat');
  const [mode, setMode] = useState<'normal' | 'interactive'>('normal');
  const [inputPlaceholder, setInputPlaceholder] = useState<string>(DEFAULT_PLACEHOLDERS.INITIAL);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [currentTaskId, setCurrentTaskId] = useState("");
  const [isTaskPaused, setIsTaskPaused] = useState(false);
  const [groupedSessions, setGroupedSessions] = useState<ChanGroupedSessions>({});
  const isTaskPausedRef = useRef(false);
  const currentTaskIdRef = useRef("");
  const [dragPosition, setDragPosition] = useState({'x': 0, 'y': 0});
  const [journeyDraft, setJourneyDraft] = useState(null);
  const journeyDraftRef = useRef(null);
  const isTaskCanceledRef = useRef(false);

  const [messageHistory, setMessageHistory] = useState<RawApiMessage[]>([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  const [isInitializing, setIsInitializing] = useState(false);
  const [suggestionMessage, setSuggestionMessage] = useState('');


  const { selectedBusiness, currentUser } = useUser();
  const { id: paramId } = useParams();

  useEffect(() => {
    const handleJBDataResponse = (event: any) => {
      const journeyData = event.detail.journey;
      if (journeyData && (journeyData.id === paramId || !journeyData.id)) {
        setJourneyDraft(journeyData);
      }
    };

    window.addEventListener("JB_DATA_RESPONSE", handleJBDataResponse);
    return () => window.removeEventListener("JB_DATA_RESPONSE", handleJBDataResponse);
  }, [paramId]);

  useEffect(() => {
      if(selectedBusiness) {
          setIsInitializing(true);
          setActiveSession({id: "temp-session", name: "Untitled", createdAt: "", updatedAt: ""});
          setChatTitle("Untitled");
          setConversations({});
          setGroupedSessions({});
          setView('chat');
          isTaskPausedRef.current = false;
          setIsTaskPaused(isTaskPausedRef.current);
          currentTaskIdRef.current = "";
          setInputPlaceholder(DEFAULT_PLACEHOLDERS.INITIAL);
          setMode('normal')
      }
  }, [selectedBusiness]);

  useEffect(() => {
    let isCancelled = false;
    if (selectedBusiness && activeSession?.id && activeSession.id !== "temp-session") {
      setIsInitializing(true);
      setIsHistoryLoading(true);
      setJourneyDraft(null);
      getChanMessageHistoryBySession(pick(selectedBusiness, ['id', 'name']), activeSession.id)
        .then(res => {
          if(isCancelled) return;
          const allMessages: RawApiMessage[] = res.tasks?.flatMap((task: any) => task.messages || []) || [];
          console.log('---Message:\n', JSON.stringify(allMessages, null, 2));
          setMessageHistory(allMessages || []);
          setConversations((prev) => {
              const updated: Record<string, ChatConversation> = {};

              Object.entries(prev).forEach(([key, convo]) => {
                  updated[key] = {
                      ...convo,
                      messages: [],
                      error: null,
                  };
              });

              return updated;
          });
          const lastTask = res.tasks.length > 0 ? res.tasks[res.tasks.length-1] : null;
          if(lastTask){
              isTaskPausedRef.current = (lastTask.status === "paused");
              setIsTaskPaused(isTaskPausedRef.current);
              setCurrentTaskId(lastTask.id);
              currentTaskIdRef.current = lastTask.id;
          }

          setSuggestionMessage('')
          setInputPlaceholder(allMessages.length > 0 ? DEFAULT_PLACEHOLDERS.FOLLOWUP : DEFAULT_PLACEHOLDERS.INITIAL)
          if (allMessages.length > 0 && allMessages[allMessages.length - 1].role === 'ai') {
            setShowFollowUp(true);
          }
        })
        .catch(error => {
          if(isCancelled) return;
          console.log("Message hist setting error: ", error);
          setMessageHistory([]);
        })
        .finally(() => {
          if(isCancelled) return;
          setIsInitializing(false);
          setIsHistoryLoading(false);
        });
    } else {
      setMessageHistory([]);
      setIsInitializing(false);
      setIsHistoryLoading(false);
    }
    return () => {
        isCancelled = true;
    }
  }, [activeSession, selectedBusiness]);

  useEffect(() => {
      journeyDraftRef.current = journeyDraft;
  }, [journeyDraft]);

  const currentConversation = useMemo((): ChatConversation | null => {
    if (!activeSession?.id) return null;

    const historyMessages: Message[] = activeSession.id === "temp-session"
      ? []
      : messageHistory.map((msg: RawApiMessage) => {
        const content = msg.content ?? "";
        const sender = msg.role;
        const text = content;
        const suggestedCustomizations: string[] | undefined = undefined;

      return {
        id: `hist_${msg.id}`,
        text,
        sender,
        timestamp: new Date(msg.created_at),
        suggestedCustomizations,
        feedback: msg.feedback,
      };
    });

    const newMessages = (activeSession?.id && conversations[activeSession.id]?.messages) || [];

    const finalMessages = [...historyMessages, ...newMessages]
    return {
      messages: finalMessages,
      loading: isHistoryLoading || (conversations[activeSession.id]?.loading ?? false) || (finalMessages[finalMessages.length-1]?.sender.includes("human") && !conversations[activeSession.id]?.error),
      error: conversations[activeSession.id]?.error || null,
    };
  }, [activeSession?.id, messageHistory, conversations, isHistoryLoading]);

  const handleCreateSession = useCallback(() => {
    setIsCreatingSession(true);
    setActiveSession({id: "temp-session", name: "Untitled", createdAt: "", updatedAt: ""});
    setChatTitle("Untitled");
    setConversations({});
    setIsCreatingSession(false);
    setView('chat');
    isTaskPausedRef.current = false;
    setIsTaskPaused(isTaskPausedRef.current);
    currentTaskIdRef.current = "";
    setInputPlaceholder(DEFAULT_PLACEHOLDERS.INITIAL);
    setJourneyDraft(null);
    journeyDraftRef.current = null;
    isTaskCanceledRef.current = false;
    setMode('normal');
  }, []);

  const handleDeleteSession = useCallback((session: ChanSession) => {
    setSessions(prev => prev.filter(s => s.id !== session.id));
    setConversations(prev => {
      const updated = { ...prev };
      delete updated[session.id];
      return updated;
    });
    if (activeSession.id === session.id) {
      setActiveSession({id:"temp-session", name: "Untitled", createdAt: "", updatedAt: ""});
    }
  }, [activeSession]);

  const pollBotResponseStatus = async (botResponse: any): Promise<any> => {
    const SECOND_TO_MS = 1000
    const INITIAL_DELAY = 5 * SECOND_TO_MS // 5s
    const POLL_INTERVAL = 3 * SECOND_TO_MS;  //3s
    const MAX_DURATION_MS = 180 * SECOND_TO_MS;
    const MAX_ATTEMPTS = MAX_DURATION_MS / POLL_INTERVAL;

    let response;
    let attempts = 0;

    // initial delay
    // await new Promise(resolve => setTimeout(resolve, INITIAL_DELAY));
    const CHUNK_MS = 500;
    let elapsed = 0;
    while (elapsed < INITIAL_DELAY) {
          if (isTaskCanceledRef.current) return { status: 'cancelled' };
          await new Promise(r => setTimeout(r, CHUNK_MS));
          elapsed += CHUNK_MS;
    }

    for (;;) {
        let pollElapsed = 0;
        while (pollElapsed < POLL_INTERVAL) {
            if (isTaskCanceledRef.current) return { status: 'cancelled' };
            await new Promise(r => setTimeout(r, CHUNK_MS));
            pollElapsed += CHUNK_MS;
        }

        response = await getChanTaskStatus(botResponse.taskId);
        if (response?.status === "completed" || response?.status === "failed" || response?.status === "paused" ||
            response?.status === "cancelled") {
            return response;
        }
        attempts++;

        if (attempts >= MAX_ATTEMPTS) {
            return {...response, status: "timeout",};
        }

        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL));

    }
  };


  const handleAddUserMessage = useCallback(async (sessionId: string, messageText: string, isFeedbackTyped: boolean = false) => {
    if (!messageText.trim() || !activeSession) return;

    const tenant = { "id" : currentUser.tenant.id, "name": currentUser.tenant.esKey };
    const sender = isTaskPausedRef.current && !isFeedbackTyped ? "human_feedback" : "human";
    const userMessage = createUserMessage(messageText, sender);

    try {
      console.log("--Session_id ---", sessionId)
      setConversations(prev => ({
          ...prev,
          [sessionId]: { ...prev[sessionId],  messages: [...(prev[sessionId]?.messages || []), userMessage], loading: true, error: null },
      }));
      isTaskCanceledRef.current = false;
      setShowFollowUp(false);

      let botResponse: ChatResponse = {taskId: "", message: "", sessionId: "", sessionName:"", status: ""};

      if(!isTaskPausedRef.current){
          botResponse= await sendChatMessage(
              tenant,
              messageText,
              activeSession.id === "temp-session" ? "" : activeSession.id,
              pick(selectedBusiness, ['id', 'name']),
              mode,
              journeyDraftRef.current,
          );
      }  else {
          const feedbackMessageText = isFeedbackTyped ? JSON.stringify({ "answers": [messageText] }) : messageText;
          botResponse = await resumeChatMessage(sessionId, feedbackMessageText, currentTaskIdRef.current, tenant, pick(selectedBusiness, ['id', 'name']), isFeedbackTyped);
      }

      setGroupedSessions({})
      if (sessionId === "temp-session") {
        const newSession = { id: botResponse.sessionId, name: "Untitled", createdAt: "", updatedAt: "" };
        setSessions(prev => [...prev, newSession]);
        setActiveSession(newSession);
        setConversations(prev => ({
              ...prev, [sessionId]: {...prev[sessionId], messages: [], loading: false, error: null }
        }));
        getNewSessionName(botResponse.sessionId, messageText)
          .then((response: any) => {
              if (!response?.sessionName) return;
              setChatTitle(response.sessionName);
              setSessions(prev =>
                  prev.map(session =>
                      session.id === botResponse.sessionId
                          ? { ...session, name: response.sessionName }
                          : session
                  )
              );
          })
          .catch(err =>
              console.log("Session title generation failed", err)
          );
      }

      setCurrentTaskId(botResponse.taskId);
      currentTaskIdRef.current = botResponse.taskId;

      const processedResponse = await pollBotResponseStatus(botResponse)

      let responseText = "";

      if (processedResponse.status === 'cancelled' || processedResponse.status === 'canceled') {
            responseText = t('context.requestCanceled');
            isTaskCanceledRef.current = false;
            setCurrentTaskId("");
            currentTaskIdRef.current = "";
      } else {
            responseText = processedResponse?.content || t('context.noResponse');
      }



      const paused = processedResponse.status === "paused";
      isTaskPausedRef.current = paused;
      setIsTaskPaused(paused);

      const botMessage = createBotMessage(responseText, [], processedResponse?.messageId);

      setConversations(prev => ({
          ...prev,
          [botResponse?.sessionId]: { ...prev[botResponse?.sessionId], messages: [...(prev[botResponse?.sessionId]?.messages || []), botMessage], loading: false },
      }));
      setInputPlaceholder(DEFAULT_PLACEHOLDERS.FOLLOWUP);
      setShowFollowUp(true);

    } catch (error) {
      // A business without its own AI provider key can't use Gargi: say so instead of a generic failure.
      const errorMessage = isProviderKeyRequired(error)
        ? PROVIDER_KEY_REQUIRED_MESSAGE
        : error instanceof Error ? error.message : 'Failed to send message';
      setConversations(prev => ({
            ...prev,  [sessionId]: { ...prev[sessionId],  loading: false, error: errorMessage },
      }));
      setCurrentTaskId("");
      currentTaskIdRef.current = "";
    }
  }, [activeSession, currentUser, selectedBusiness, mode, t]);

  const handleResetChat = useCallback(() => {
    handleCreateSession();
  }, [handleCreateSession]);

  const handleCopyToClipboard = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('context.copied'));
    } catch (error) {
      toast.error(t('context.copyFailed'));
      console.error('Clipboard copy failed:', error);
    }
  }, [t]);

  const handleRegenerateChat = useCallback( async () => {
      try {
          const messages = currentConversation?.messages || [];
          const lastUserMessage = messages.at(-1)?.text ?? "";

          if (!lastUserMessage) {
              toast.error(t('context.noMessageToRegenerate'));
              return;
          }
          setConversations(prev => ({
              ...prev,
              [activeSession?.id]: { ...prev[activeSession?.id],  messages: [], loading: false, error: null },
          }));
          await handleAddUserMessage(activeSession?.id ?? "", lastUserMessage);
      } catch (error) {
          toast.error(t('context.regenerateFailed'));
          console.error('Regenerate Chat failed: ', error);
      }
  }, [currentConversation, activeSession, handleAddUserMessage, t])

  const handleCancelTask = useCallback(async () => {
      const taskId = currentTaskIdRef.current;
      if (!taskId) return;

      isTaskCanceledRef.current = true;
      setCurrentTaskId("");

      try {
          await cancelTask(taskId);
      } catch (error) {
          console.error('Cancel task failed:', error);
      }
  }, []);

  const contextValue: DraggableChatContext = {
    currentConversation,
    isInitializing,
    sessions,
    setSessions,
    activeSession,
    setActiveSession,
    isCreatingSession,
    createSessionError,
    chatTitle,
    setChatTitle,
    view,
    setView,
    mode,
    setMode,
    inputPlaceholder,
    setInputPlaceholder,
    showFollowUp,
    setShowFollowUp,
    isMinimized,
    setIsMinimized,
    createSession: handleCreateSession,
    deleteSession: handleDeleteSession,
    addUserMessage: handleAddUserMessage,
    resetChat: handleResetChat,
    copyMessageToClipboard: handleCopyToClipboard,
    currentTaskId,
    setCurrentTaskId,
    isTaskPaused,
    setIsTaskPaused,
    suggestionMessage,
    setSuggestionMessage,
    groupedSessions,
    setGroupedSessions,
    regenerateChat: handleRegenerateChat,
    cancelTask: handleCancelTask,
    dragPosition,
    setDragPosition,
    journeyDraft,
    setJourneyDraft,
  };

  return <DraggableChatContextValue.Provider value={contextValue}>{children}</DraggableChatContextValue.Provider>;
};

export default DraggableChatContextValue;
